import { Toast } from '@byecode/ui'
import type { ViewBlockAbstract } from '@lighthouse/core'
import {
    findBlockById,
    getMainTableRecordId,
    getQuickFilterRule,
    getWithScopeId,
    PrintModal,
    SuspendPagination,
    useApplicationContext,
    useAtomAction,
    useAtomData,
    useLanguageContext
} from '@lighthouse/shared'
import { useAtomCallback } from 'jotai/utils'
import { find } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { syncComponentsAtom } from '@/atoms/application/state'
import { viewIndependentDataAtomFamily } from '@/atoms/dataSource/state'
import { clearViewRuntimeStateAtom } from '@/atoms/page/action'
import { lastPageOfStackAtom, pageBlocksAtom, viewRuntimeStateAtomFamily } from '@/atoms/page/state'
import { quickFilterCacheAtomFamily, sortsCacheAtomFamily } from '@/atoms/storage/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useFilter } from '@/hooks/useFilter'
import { useLinkFilter } from '@/hooks/useLinkFilter'
import * as srv from '@/services'

const SCxSuspendWrapper = styled.div`
    position: sticky;
    bottom: 24px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 200;
`

export const PageSuspendPagination: React.FC = () => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const [pageId, stackId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.pageId || '', s?.stackId || ''] as const, [])
    )
    const { personOptions } = useApplicationContext()
    const [printModalOpen, setPrintModalOpen] = useState(false)
    const dataSourceList = useDataSourceList(appId, envId)
    const { convertTextByLanguage } = useLanguageContext()

    const selectState = useAtomData(viewRuntimeStateAtomFamily(stackId))

    const scope = selectState?.scope

    const viewBlock = useAtomData(
        scope ? syncComponentsAtom : pageBlocksAtom(pageId),
        useCallback(s => (selectState?.viewId ? findBlockById<ViewBlockAbstract>(selectState.viewId, s) : undefined), [selectState?.viewId])
    )
    const withScopeId = useMemo(() => (viewBlock?.id ? getWithScopeId(viewBlock.id, scope) : ''), [scope, viewBlock?.id])
    const rowTotal = useAtomData(
        viewIndependentDataAtomFamily({ stackId, dsId: viewBlock?.config.pointer || '', viewId: withScopeId }),
        useCallback(s => s.rowTotal ?? 0, [])
    )
    const linkFilterController = useMemo(() => viewBlock?.config.linkFilterController, [viewBlock?.config.linkFilterController])

    const getSorts = useAtomCallback(
        useCallback(
            get => {
                if (!viewBlock) {
                    return []
                }
                const { id, config } = viewBlock
                const { canSort } = config
                return canSort ? get(sortsCacheAtomFamily({ appId, envId, id })) : []
            },
            [appId, envId, viewBlock]
        )
    )

    // 筛选器hook
    const { resolvedFilter } = useFilter({
        filter: viewBlock?.config.filter,
        dataSourceList
    })

    // 联动筛选器hook
    const { resolvedLinkFilter } = useLinkFilter({
        appId,
        envId,
        pageId,
        linkFilterController
    })

    const getQuickFilters = useAtomCallback(
        useCallback(
            get => {
                if (!viewBlock) {
                    return
                }
                const { config, id } = viewBlock
                const { pointer, quickFilter } = config
                const dataSource = find(item => item.id === pointer, dataSourceList)
                if (!dataSource) {
                    return []
                }
                const quickFiltersCache = get(quickFilterCacheAtomFamily({ appId, envId, id }))
                return getQuickFilterRule({ quickFiltersCache, quickFilter, dataSource, personOptions })
            },
            [appId, dataSourceList, envId, personOptions, viewBlock]
        )
    )

    const handleGetExportTemplateList = useCallback(async () => {
        const pointer = viewBlock?.config.pointer
        if (!pointer) {
            return []
        }
        const list = await srv.getPrintTemplateList(pointer)
        if (!list) {
            return []
        }
        return list.map(item => ({ value: item.templateId, label: item.name }))
    }, [viewBlock?.config.pointer])

    const { run: clearViewRuntimeState } = useAtomAction(clearViewRuntimeStateAtom)
    const handleClose = useCallback(() => {
        clearViewRuntimeState(stackId)
    }, [clearViewRuntimeState, stackId])

    // const handleDelete = useCallback(async () => {
    //     if (!viewBlock) {
    //         return false
    //     }
    //     const recordIds = selectState?.selectedIds || []
    //     const { config, id } = viewBlock
    //     const { pointer, search } = config
    //     const isConfirm = await Modal.confirm({
    //         title: convertTextByLanguage('sureDelete'),
    //         content: convertTextByLanguage('deleteRecordWaring', {
    //             count: selectState?.selectedMode === 'ALL' ? rowTotal : recordIds.length
    //         }),
    //         cancelText: convertTextByLanguage('cancel'),
    //         okText: convertTextByLanguage('delete'),
    //         okStatus: 'error'
    //     })
    //     if (isConfirm) {
    //         const isDelete =
    //             selectState?.selectedMode === 'ALL'
    //                 ? await deleteViewRecord({
    //                       envId,
    //                       dsId: pointer,
    //                       viewId: id,
    //                       isSyncComponent: !!scope,
    //                       recordIds,
    //                       mode: selectState?.selectedMode,
    //                       filter: resolvedFilter,
    //                       quickFilters: getQuickFilters(),
    //                       linkFilter: resolvedLinkFilter,
    //                       search
    //                   })
    //                 : await deleteRecord({ envId, dsId: pointer, recordIds, mode: selectState?.selectedMode })
    //         if (isDelete) {
    //             handleClose()
    //             pageStackPubSub.emit(`${pointer}-DELETE`)
    //         }
    //         return isDelete
    //     }
    //     return false
    // }, [
    //     viewBlock,
    //     selectState?.selectedIds,
    //     selectState?.selectedMode,
    //     convertTextByLanguage,
    //     rowTotal,
    //     deleteViewRecord,
    //     envId,
    //     scope,
    //     resolvedFilter,
    //     getQuickFilters,
    //     resolvedLinkFilter,
    //     deleteRecord,
    //     handleClose
    // ])

    const handlePrintByTemplate = useCallback(
        async (templateId: string) => {
            if (!viewBlock) {
                return
            }
            Toast.warning(convertTextByLanguage('exportingOfProgress'))
            const { config } = viewBlock
            const { pointer, search } = config
            const recordIds = [...new Set(selectState?.selectedIds?.map(recordId => getMainTableRecordId(recordId)))]
            const params = {
                appId,
                dsId: pointer,
                pageId,
                recordIds,
                templateId,
                mode: selectState?.selectedMode,
                sorts: getSorts(),
                search,
                filter: resolvedFilter,
                quickFilters: getQuickFilters(),
                linkFilter: resolvedLinkFilter
            }
            const res = await srv.printByTemplate(params)
            const { data, headers } = res
            const fileName = headers?.['content-disposition']?.replace?.("attachment;filename*=utf-8''", '') ?? '导出文件.pdf'
            const dom = document.createElement('a')
            const url = window.URL.createObjectURL(data)
            dom.href = url
            dom.download = decodeURI(fileName)
            dom.style.display = 'none'
            document.body.append(dom)
            dom.click()
            dom.remove()
            window.URL.revokeObjectURL(url)
        },
        [
            appId,
            convertTextByLanguage,
            getQuickFilters,
            getSorts,
            pageId,
            resolvedFilter,
            resolvedLinkFilter,
            selectState?.selectedIds,
            selectState?.selectedMode,
            viewBlock
        ]
    )

    const handleExport = useCallback(async () => {
        if (!viewBlock) {
            return
        }
        const { id, config, title } = viewBlock
        const { pointer, search } = config
        // const dsId = viewBlock.config.appId
        Toast.warning(convertTextByLanguage('exportingOfProgress'))
        const recordIds = [...new Set(selectState?.selectedIds?.map(recordId => getMainTableRecordId(recordId)))]
        const res = await srv.exportView({
            viewId: id,
            isSyncComponent: !!scope,
            recordIds,
            mode: selectState?.selectedMode,
            fileType: 'xlsx',
            sorts: getSorts(),
            filter: resolvedFilter,
            linkFilter: resolvedLinkFilter,
            search,
            quickFilters: getQuickFilters()
        })
        const { data, headers } = res
        const fileName = headers?.['content-disposition']?.replace?.("attachment;filename*=utf-8''", '') ?? `${title}.xlsx`
        const dom = document.createElement('a')
        const url = window.URL.createObjectURL(data)
        dom.href = url
        dom.download = decodeURI(fileName)
        dom.style.display = 'none'
        document.body.append(dom)
        dom.click()
        dom.remove()
        window.URL.revokeObjectURL(url)
    }, [
        convertTextByLanguage,
        getQuickFilters,
        getSorts,
        resolvedFilter,
        resolvedLinkFilter,
        scope,
        selectState?.selectedIds,
        selectState?.selectedMode,
        viewBlock
    ])

    const handlePrintModalClose = useCallback(() => {
        setPrintModalOpen(false)
    }, [setPrintModalOpen])

    return useMemo(() => {
        if (!viewBlock || viewBlock.type !== 'view' || !selectState?.selectedIds || selectState.selectedIds.length === 0) {
            return null
        }
        const enableExport = viewBlock.config.canExport
        const enablePrint = viewBlock.config.canPrint
        return (
            <>
                <SCxSuspendWrapper data-ignore-click-away>
                    <SuspendPagination
                        mode={selectState?.selectedMode}
                        total={rowTotal}
                        selectIds={selectState?.selectedIds}
                        enablePrint={enablePrint}
                        enableExport={enableExport}
                        // onDelete={handleDelete}
                        onPrint={() => setPrintModalOpen(true)}
                        onExport={handleExport}
                        onClose={handleClose}
                    />
                </SCxSuspendWrapper>
                <PrintModal
                    open={printModalOpen}
                    onGetExportTemplateList={handleGetExportTemplateList}
                    onPrint={handlePrintByTemplate}
                    onClose={handlePrintModalClose}
                />
            </>
        )
    }, [
        handleClose,
        // handleDelete,
        handleExport,
        handleGetExportTemplateList,
        handlePrintByTemplate,
        handlePrintModalClose,
        printModalOpen,
        rowTotal,
        selectState?.selectedIds,
        selectState?.selectedMode,
        viewBlock
    ])
}
